/**
 * Mock data for building/previewing the UI without a live backend.
 *
 * Shapes intentionally mirror the planned API + the new `Conversation`/`Message`
 * DB models (platform, direction, delivery status, AI-generated replies) so that
 * when the real endpoints land, wiring is a drop-in swap. Toggle with
 * REACT_APP_USE_MOCKS ("false" disables). See lib/mockConfig.ts.
 */
import type { Conversation, Message } from '@/types/api';

export interface CustomerProfile {
  name: string;
  email: string;
  phone: string;
  location: string;
  tags: string[];
  totalOrders: number;
  totalSpent: string;
  lastSeen: string; // ISO
  notes: string;
}

const iso = (minsAgo: number) => new Date(Date.now() - minsAgo * 60_000).toISOString();

export const mockConversations: Conversation[] = [
  {
    id: 'c1',
    customerName: 'Ayesha Rahman',
    platform: 'whatsapp',
    lastMessage: 'Do you have the red kurti in size M? Need it before Eid 🙏',
    timestamp: iso(3),
    status: 'active',
    priority: 'high',
    unreadCount: 2,
  },
  {
    id: 'c2',
    customerName: 'Tanvir Ahmed',
    platform: 'facebook',
    lastMessage: 'Thanks! Order placed. When will it ship?',
    timestamp: iso(18),
    status: 'active',
    priority: 'medium',
    unreadCount: 0,
  },
  {
    id: 'c3',
    customerName: 'Sadia Islam',
    platform: 'instagram',
    lastMessage: 'Is the leather tote available in black?',
    timestamp: iso(42),
    status: 'pending',
    priority: 'urgent',
    unreadCount: 5,
  },
  {
    id: 'c4',
    customerName: 'Rifat Chowdhury',
    platform: 'facebook',
    lastMessage: 'Can I get a discount for bulk order of 20 mugs?',
    timestamp: iso(95),
    status: 'active',
    priority: 'high',
    unreadCount: 1,
  },
  {
    id: 'c5',
    customerName: 'Nusrat Jahan',
    platform: 'whatsapp',
    lastMessage: 'Perfect, thank you for the quick help! ⭐',
    timestamp: iso(180),
    status: 'resolved',
    priority: 'low',
    unreadCount: 0,
  },
  {
    id: 'c6',
    customerName: 'Mahin Khan',
    platform: 'instagram',
    lastMessage: 'What are the shipping charges to Chittagong?',
    timestamp: iso(320),
    status: 'pending',
    priority: 'medium',
    unreadCount: 0,
  },
  {
    id: 'c7',
    customerName: 'Farhana Akter',
    platform: 'facebook',
    lastMessage: 'Do you accept bKash payment?',
    timestamp: iso(710),
    status: 'resolved',
    priority: 'low',
    unreadCount: 0,
  },
  {
    id: 'c8',
    customerName: 'Imran Hossain',
    platform: 'whatsapp',
    lastMessage: 'My order #1042 arrived damaged, I need a replacement.',
    timestamp: iso(1440),
    status: 'active',
    priority: 'urgent',
    unreadCount: 3,
  },
];

const m = (
  conversationId: string,
  id: string,
  direction: Message['direction'],
  content: string,
  minsAgo: number,
  status: Message['status'] = 'read',
): Message => ({
  id,
  conversationId,
  content,
  direction,
  timestamp: iso(minsAgo),
  status,
  type: 'text',
});

export const mockMessages: Record<string, Message[]> = {
  c1: [
    m('c1', 'c1m1', 'inbound', 'Hi! Do you have the red kurti in size M?', 12),
    m('c1', 'c1m2', 'outbound', 'Hello Ayesha! 👋 Yes, the Red Embroidered Kurti is in stock in size M — ৳1,450. Would you like me to reserve one for you?', 11),
    m('c1', 'c1m3', 'inbound', 'Yes please! Need it before Eid 🙏', 4),
    m('c1', 'c1m4', 'outbound', 'Reserved! ✅ We can deliver within 2 days — well before Eid. Shall I confirm the order to your Dhaka address?', 3, 'delivered'),
  ],
  c2: [
    m('c2', 'c2m1', 'inbound', 'I want to order the ceramic mug set', 30),
    m('c2', 'c2m2', 'outbound', 'Great choice! The 4-piece Ceramic Mug Set is ৳980. I can place the order now — cash on delivery or bKash?', 28),
    m('c2', 'c2m3', 'inbound', 'bKash please', 22),
    m('c2', 'c2m4', 'outbound', 'Order placed ✅ Payment link sent. Thank you, Tanvir!', 20),
    m('c2', 'c2m5', 'inbound', 'Thanks! Order placed. When will it ship?', 18),
  ],
  c3: [
    m('c3', 'c3m1', 'inbound', 'Is the leather tote available in black?', 45),
    m('c3', 'c3m2', 'outbound', 'Hi Sadia! The Classic Leather Tote comes in Black, Tan, and Maroon — all in stock. Black is ৳2,300. Want to see photos?', 43, 'sent'),
  ],
  c4: [
    m('c4', 'c4m1', 'inbound', 'Can I get a discount for bulk order of 20 mugs?', 96),
    m('c4', 'c4m2', 'outbound', 'For 20+ mugs we offer 15% off — that brings the set to ৳833 each. Shall I prepare a bulk quote?', 95, 'read'),
  ],
  c5: [
    m('c5', 'c5m1', 'inbound', 'Where is my order? #1039', 200),
    m('c5', 'c5m2', 'outbound', 'Your order #1039 is out for delivery and should arrive today by 6 PM 🚚', 195),
    m('c5', 'c5m3', 'inbound', 'Perfect, thank you for the quick help! ⭐', 180),
  ],
  c6: [
    m('c6', 'c6m1', 'inbound', 'What are the shipping charges to Chittagong?', 320),
    m('c6', 'c6m2', 'outbound', 'Shipping to Chittagong is ৳120 and takes 3–4 days. Inside Dhaka it is ৳60. 😊', 318, 'delivered'),
  ],
  c7: [
    m('c7', 'c7m1', 'inbound', 'Do you accept bKash payment?', 715),
    m('c7', 'c7m2', 'outbound', 'Yes! We accept bKash, Nagad, and cash on delivery. 💳', 712),
  ],
  c8: [
    m('c8', 'c8m1', 'inbound', 'My order #1042 arrived damaged, I need a replacement.', 1445),
    m('c8', 'c8m2', 'outbound', 'I am so sorry to hear that, Imran. 😔 Could you share a photo of the damage? I will arrange a free replacement right away.', 1442, 'failed'),
  ],
};

export const mockProfiles: Record<string, CustomerProfile> = {
  c1: { name: 'Ayesha Rahman', email: 'ayesha.r@gmail.com', phone: '+880 1712 345678', location: 'Dhaka, Bangladesh', tags: ['VIP', 'Returning'], totalOrders: 8, totalSpent: '৳12,400', lastSeen: iso(3), notes: 'Prefers WhatsApp. Buys ethnic wear around festivals.' },
  c2: { name: 'Tanvir Ahmed', email: 'tanvir.ahmed@outlook.com', phone: '+880 1819 654321', location: 'Dhaka, Bangladesh', tags: ['New'], totalOrders: 1, totalSpent: '৳980', lastSeen: iso(18), notes: 'First-time buyer via Facebook page.' },
  c3: { name: 'Sadia Islam', email: 'sadia.islam@gmail.com', phone: '+880 1913 112233', location: 'Sylhet, Bangladesh', tags: ['High-intent'], totalOrders: 3, totalSpent: '৳6,900', lastSeen: iso(42), notes: 'Loves leather goods. Responsive to photos.' },
  c4: { name: 'Rifat Chowdhury', email: 'rifat.c@company.com', phone: '+880 1611 445566', location: 'Chittagong, Bangladesh', tags: ['Wholesale', 'VIP'], totalOrders: 5, totalSpent: '৳48,000', lastSeen: iso(95), notes: 'Corporate gifting buyer — bulk orders.' },
  c5: { name: 'Nusrat Jahan', email: 'nusrat.j@gmail.com', phone: '+880 1521 778899', location: 'Dhaka, Bangladesh', tags: ['Returning'], totalOrders: 6, totalSpent: '৳9,300', lastSeen: iso(180), notes: 'Happy customer, leaves reviews.' },
  c6: { name: 'Mahin Khan', email: 'mahin.khan@gmail.com', phone: '+880 1770 223344', location: 'Chittagong, Bangladesh', tags: ['New'], totalOrders: 0, totalSpent: '৳0', lastSeen: iso(320), notes: 'Asking about shipping — not yet purchased.' },
  c7: { name: 'Farhana Akter', email: 'farhana.a@yahoo.com', phone: '+880 1998 556677', location: 'Khulna, Bangladesh', tags: ['Returning'], totalOrders: 2, totalSpent: '৳3,100', lastSeen: iso(710), notes: 'Payment-method questions; prefers bKash.' },
  c8: { name: 'Imran Hossain', email: 'imran.h@gmail.com', phone: '+880 1345 998877', location: 'Dhaka, Bangladesh', tags: ['Support', 'VIP'], totalOrders: 11, totalSpent: '৳21,500', lastSeen: iso(1440), notes: 'Damaged-order case open — replacement pending.' },
};

// ---- Dashboard mock (fallback when the API is unavailable) ----
export const mockDashboardOverview = {
  totalConversations: 1284,
  activeConversations: 37,
  totalLeads: 216,
  newLeadsToday: 14,
  connectedPlatforms: 3,
  aiResponseRate: 0.92,
  avgResponseTimeSec: 8,
  messagesByPlatform: { facebook: 540, instagram: 310, whatsapp: 434 },
  recentActivity: [
    { id: 'a1', type: 'new_lead', text: 'New high-priority lead from Sadia Islam (Instagram)', at: iso(42) },
    { id: 'a2', type: 'ai_reply', text: 'AI replied to Ayesha Rahman on WhatsApp', at: iso(3) },
    { id: 'a3', type: 'order', text: 'Tanvir Ahmed placed an order via Messenger', at: iso(20) },
  ],
};

export const mockLiveStats = {
  onlineNow: 12,
  messagesLastHour: 48,
  aiRepliesLastHour: 44,
  leadsLastHour: 3,
};

export const mockLeads = [
  { id: 'l1', customerName: 'Sadia Islam', message: 'Is the leather tote available in black?', platform: 'instagram', status: 'new', priority: 'high', source: 'instagram_dm', createdAt: iso(42), updatedAt: iso(42) },
  { id: 'l2', customerName: 'Rifat Chowdhury', message: 'Bulk order of 20 mugs — need a quote', platform: 'facebook', status: 'contacted', priority: 'high', source: 'facebook_page', createdAt: iso(95), updatedAt: iso(60) },
  { id: 'l3', customerName: 'Ayesha Rahman', message: 'Red kurti size M before Eid', platform: 'whatsapp', status: 'qualified', priority: 'medium', source: 'whatsapp', createdAt: iso(12), updatedAt: iso(3) },
  { id: 'l4', customerName: 'Mahin Khan', message: 'Shipping charges to Chittagong?', platform: 'instagram', status: 'new', priority: 'low', source: 'instagram_dm', createdAt: iso(320), updatedAt: iso(320) },
  { id: 'l5', customerName: 'Imran Hossain', message: 'Damaged order replacement #1042', platform: 'whatsapp', status: 'new', priority: 'urgent', source: 'whatsapp', createdAt: iso(1440), updatedAt: iso(1440) },
];
